import type { LogicOperand } from './logicOperand'

export interface LogicOperandPickerProps {
  operand: LogicOperand
  onChange: (next: LogicOperand) => void
  /** Current record's `indicator` name — shown next to the "indicator"
   * option, matching legacy (`indicator ({{ momentum.indicator }})`). */
  indicatorName: string
  /** Field names available for `type === 'indicator'`, resolved from the
   * matching entry of `GET /indicators/{exchangeId}/{tradingType}/-/-...`. */
  possibleFields: string[]
  /** `Momentum.source` (`'cd'` = live candle, anything else = closed price).
   * `IndicatorCondition` has no `source` field at all in the legacy model
   * (`data/model/momentum.ts`) — its template read `indicatorCondition.source`
   * anyway, which was always `undefined`, so its candle/lastValue field list
   * always rendered the closed-price-only subset. Pass `undefined` from
   * `IndicatorconditionEditPage` to reproduce that exactly. */
  source?: string | null
}

/**
 * Port of the legacy `LogicOperand` type/field/value/index picker UI,
 * duplicated verbatim in `momentum-edit.component.html` and
 * `indicatorcondition-edit.component.html` (three `<select>`/`<input>`
 * columns per operand). Persists to the exact same `logicOperand1`/
 * `logicOperand2` string format via `logicOperandToString` — this is a UI
 * port only, not a data-shape change (see `logicOperand.ts`).
 */
export function LogicOperandPicker({ operand, onChange, indicatorName, possibleFields, source }: LogicOperandPickerProps) {
  const isCandleSource = source === 'cd'

  return (
    <div className="screener-operand-row">
      <select
        aria-label="Operand type"
        value={operand.type}
        onChange={(e) => onChange({ ...operand, type: e.target.value as LogicOperand['type'] })}
      >
        <option value="">choose …</option>
        <option value="indicator">indicator ({indicatorName})</option>
        <option value="candle">{isCandleSource ? 'candle' : 'closed price'}</option>
        <option value="constant">constant value</option>
      </select>

      {operand.type === 'indicator' && (
        <select aria-label="Operand field" value={operand.field} onChange={(e) => onChange({ ...operand, field: e.target.value })}>
          <option value="">choose …</option>
          {possibleFields.map((field) => (
            <option key={field} value={field}>
              {field}
            </option>
          ))}
        </select>
      )}

      {(operand.type === 'candle' || operand.type === 'lastValue') && (
        <select aria-label="Operand field" value={operand.field} onChange={(e) => onChange({ ...operand, field: e.target.value })}>
          <option value="">choose …</option>
          {isCandleSource && <option value="open">open price</option>}
          <option value="close">close price</option>
          {isCandleSource && <option value="low">low price</option>}
          {isCandleSource && <option value="high">high price</option>}
          {isCandleSource && <option value="volume">volume</option>}
        </select>
      )}

      {operand.type === 'constant' && (
        <input
          aria-label="Operand value"
          type="text"
          placeholder="value"
          value={operand.value}
          onChange={(e) => onChange({ ...operand, value: e.target.value })}
        />
      )}

      {(operand.type === 'indicator' || operand.type === 'candle') && (
        <input
          aria-label="Operand index"
          type="number"
          min={0}
          max={249}
          placeholder="index"
          value={operand.index}
          onChange={(e) => onChange({ ...operand, index: parseInt(e.target.value, 10) || 0 })}
        />
      )}
    </div>
  )
}
